import { inject, Injectable } from '@angular/core';
import {
  ImageStoreService,
  type UploadImageRequest,
} from '@imagise/generated-imagise-cloud-api-client';
import { v4 as uuidV4 } from 'uuid';
import { Observable, concat, map, of, switchMap } from 'rxjs';
import { UploadInfo } from './upload-info.interface';
import { FileResult } from '../core';
import { ObjectUrlHolder, readFile } from '@imagise/shared';
import { assertSuccessfulOperationResponse } from '../error-reporting';
@Injectable({
  providedIn: 'root',
})
export class UploadService {
  #imageStoreService = inject(ImageStoreService);
  readonly #localObjectUrl = new ObjectUrlHolder();

  constructor() {
    this.uploadId = uuidV4();
  }

  /**
   * Returns upload ID
   */
  uploadId: string;

  /**
   * Uploads specified file
   * @param file
   */
  uploadFile(file: File): Observable<UploadInfo> {
    const initialState: UploadInfo = { state: 'PENDING', progress: 0 };
    const uploadRequest$ = readFile(file).pipe(
      map(dataUrl => this.extractBase64Payload(dataUrl)),
      switchMap(base64Payload => {
        const body: UploadImageRequest = {
          imageFileAsBase64: base64Payload,
        };

        return this.#imageStoreService.upload(body).pipe(
          map(uploadResponse => {
            assertSuccessfulOperationResponse(uploadResponse);
            const rawUploadId = this.extractUploadIdFromResponse(uploadResponse);
            return this.createFileResult(file, base64Payload, this.normalizeUploadId(rawUploadId));
          })
        );
      }),
      map(fileInfo => ({
        progress: 100,
        state: 'DONE' as const,
        fileInfo,
      }))
    );

    return concat(of(initialState), uploadRequest$);
  }

  uploadLocalFile(file: File): Observable<UploadInfo> {
    const initialState: UploadInfo = { state: 'PENDING', progress: 0 };
    const uploadRequest$ = readFile(file).pipe(
      map(dataUrl => this.createFileResult(file, this.extractBase64Payload(dataUrl), this.uploadId)),
      map(fileInfo => ({
        progress: 100,
        state: 'DONE' as const,
        fileInfo,
      }))
    );

    return concat(of(initialState), uploadRequest$);
  }

  /** Updates the upload ID */
  updateUploadId(): void {
    this.#localObjectUrl.revoke();
    this.uploadId = uuidV4();
  }

  private createFileResult(file: File, sourceBase64: string, uploadId: string): FileResult {
    const effectiveUploadId = uploadId || this.uploadId;
    return {
      IsSuccess: true,
      idError: '',
      id: effectiveUploadId,
      FileName: file.name,
      UploadFileName: '',
      idUpload: effectiveUploadId,
      FileLength: file.size,
      SourceBase64: sourceBase64,
      SourceMimeType: file.type || 'application/octet-stream',
      LocalObjectUrl: this.#localObjectUrl.create(file),
    };
  }

  private extractBase64Payload(dataUrl: string): string {
    const splitIndex = dataUrl.indexOf(',');
    return splitIndex >= 0 ? dataUrl.slice(splitIndex + 1) : dataUrl;
  }

  private normalizeUploadId(rawUploadId: string): string {
    const trimmed = rawUploadId.trim();
    if (!trimmed) {
      return this.uploadId;
    }

    const unquoted =
      trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 1 ? trimmed.slice(1, -1) : trimmed;
    const isSimpleIdentifier = /^[a-zA-Z0-9\-_.:]+$/.test(unquoted);

    return isSimpleIdentifier ? unquoted : this.uploadId;
  }

  private extractUploadIdFromResponse(uploadResponse: unknown): string {
    if (typeof uploadResponse === 'string') {
      return uploadResponse;
    }

    if (uploadResponse && typeof uploadResponse === 'object') {
      const response = uploadResponse as Record<string, unknown>;
      const fileId = response['FileId'] ?? response['fileId'];

      if (typeof fileId === 'string') {
        return fileId;
      }
    }

    return '';
  }
}
